import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Spinner from "../common/Spinner";
import EmailDraftModal from "../email/EmailDraftModal";
import { getLeadConversation, logLeadTouch, deleteLeadTouch, leadCampaignAction } from "../../api/leads";
import { createNudge } from "../../api/email";
import { telHref, smsHref, firstName, senderName } from "../../utils/contact";
import { formatEasternDateTime } from "../../utils/dates";
import { campaignLine } from "../../utils/campaign";

// One lead's whole conversation: what they asked, every email that went to
// them, the quote, and the calls, texts and replies you log. Logging that they
// replied stops the no-reply follow-ups.

const LOG_KINDS = [
  { kind: "call", label: "Call" },
  { kind: "text", label: "Text" },
  { kind: "email", label: "Email" },
  { kind: "note", label: "Note" },
];

export default function LeadConversation({ leadId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [logging, setLogging] = useState(null); // {kind, direction, note}
  const [email, setEmail] = useState(null); // {id, note}: a reply open in the editor
  const [changed, setChanged] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await getLeadConversation(leadId));
      setError("");
    } catch (e) {
      setError(e.message || "Couldn't load the conversation.");
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  async function act(label, fn) {
    setBusy(label);
    setError("");
    try {
      setData(await fn());
      setChanged(true);
      return true;
    } catch (e) {
      setError(e.message || "That didn't work.");
      return false;
    } finally {
      setBusy("");
    }
  }

  async function saveLog(e) {
    e.preventDefault();
    const ok = await act("log", () => logLeadTouch(leadId, logging));
    if (ok) setLogging(null);
  }

  async function writeReply() {
    setBusy("reply");
    setError("");
    try {
      const d = await createNudge({ kind: "nudge_lead", lead_id: leadId });
      setEmail({ id: d.id, note: d.ai_note || "" });
    } catch (e) {
      setError(e.message || "Couldn't write that email.");
    } finally {
      setBusy("");
    }
  }

  if (email) {
    return (
      <EmailDraftModal
        draftId={email.id}
        note={email.note}
        onClose={(didChange) => {
          setEmail(null);
          if (didChange) setChanged(true);
          load();
        }}
      />
    );
  }

  const lead = data?.lead;
  const name = lead ? lead.name || lead.email || lead.phone || "Lead" : "Conversation";

  return (
    <Modal isOpen title={name} onClose={() => onClose(changed)} maxWidthClass="max-w-xl">
      {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {!data ? (
        !error && (
          <div className="py-12 flex justify-center">
            <Spinner label="Loading..." />
          </div>
        )
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-1.5">
            {telHref(lead.phone) && <Chip href={telHref(lead.phone)}>Call</Chip>}
            {lead.phone && (
              <Chip
                href={smsHref(
                  lead.phone,
                  `Hi ${firstName(lead.name)}, it's ${senderName() || "the team"} from Birdie Bus. Thanks for reaching out!`
                )}
              >
                Text
              </Chip>
            )}
            {lead.customer_id && <Chip to={`/admin/customers/${lead.customer_id}`}>Customer page</Chip>}
            <div className="flex-1" />
            {lead.email && lead.stage === "new" && (
              <Button size="sm" onClick={writeReply} disabled={Boolean(busy)}>
                {busy === "reply" ? "Writing…" : data.open_draft_id ? "Open your reply" : data.ai_enabled ? "Write a reply ✨" : "Write a reply"}
              </Button>
            )}
          </div>

          <Campaign
            lead={lead}
            busy={busy}
            onAction={(action) => act(action, () => leadCampaignAction(leadId, action))}
            onReview={(id) => setEmail({ id, note: "" })}
          />

          <div className="rounded-xl border border-birdie-green/15 p-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <Button
                size="sm"
                onClick={() => setLogging({ kind: "replied", direction: "in", note: "" })}
                disabled={Boolean(busy)}
              >
                They replied
              </Button>
              <span className="text-[11px] text-birdie-charcoal/50 px-1">Log:</span>
              {LOG_KINDS.map((k) => (
                <button
                  key={k.kind}
                  type="button"
                  onClick={() => setLogging({ kind: k.kind, direction: k.kind === "note" ? null : "out", note: "" })}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${
                    logging?.kind === k.kind
                      ? "bg-birdie-green text-white border-birdie-green"
                      : "border-birdie-green/20 text-birdie-charcoal/75 hover:bg-birdie-cream"
                  }`}
                >
                  {k.label}
                </button>
              ))}
            </div>
            {logging && (
              <form onSubmit={saveLog} className="mt-3 space-y-2">
                {logging.kind === "replied" ? (
                  <p className="text-xs text-birdie-charcoal/65">
                    Stops the no-reply follow-ups (and withdraws one waiting for approval). Add what they said if it
                    helps you later.
                  </p>
                ) : (
                  logging.kind !== "note" && (
                    <div className="flex gap-1 rounded-lg bg-birdie-cream p-1 w-fit">
                      {[
                        ["out", "From you"],
                        ["in", "From them"],
                      ].map(([dir, label]) => (
                        <button
                          key={dir}
                          type="button"
                          onClick={() => setLogging((l) => ({ ...l, direction: dir }))}
                          className={`px-3 py-1 rounded-md text-xs font-semibold min-h-0 ${
                            logging.direction === dir ? "bg-white shadow-sm text-birdie-charcoal" : "text-birdie-charcoal/55"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )
                )}
                <textarea
                  value={logging.note}
                  onChange={(e) => setLogging((l) => ({ ...l, note: e.target.value }))}
                  rows={2}
                  maxLength={2000}
                  placeholder={logging.kind === "replied" ? "e.g. Wants Dec 12, 40 guests" : "What happened (optional)"}
                  aria-label="Note"
                  className="w-full rounded-xl border border-birdie-green/20 bg-white px-3 py-2 text-sm"
                />
                {logging.direction === "in" && logging.kind !== "replied" && (
                  <p className="text-[11px] text-birdie-charcoal/50">Anything from them stops the no-reply follow-ups.</p>
                )}
                <div className="flex gap-2">
                  <Button type="submit" size="sm" disabled={busy === "log"}>
                    {busy === "log" ? "Saving…" : "Save"}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setLogging(null)}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>

          <Timeline
            items={data.items}
            onUndo={(item) =>
              window.confirm("Remove this from the conversation?") &&
              act("undo", () => deleteLeadTouch(leadId, item.touch_id))
            }
          />
        </div>
      )}
    </Modal>
  );
}

function Campaign({ lead, busy, onAction, onReview }) {
  const c = lead.campaign || {};
  const line = campaignLine(lead);
  if (!c.enabled) {
    return (
      <p className="text-xs text-birdie-charcoal/55">
        No-reply follow-ups are off.{" "}
        <Link to="/admin/email" className="font-semibold text-birdie-green underline">
          Set them up
        </Link>
      </p>
    );
  }
  const canStart = lead.email && lead.stage !== "lost" && lead.stage !== "won" && c.status !== "active";
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl bg-birdie-cream/70 px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-xs font-semibold text-birdie-charcoal/70">No-reply follow-ups</div>
        <div className="text-sm text-birdie-charcoal">{line || "Not running."}</div>
        {c.status === "active" && c.next_subject && (
          <div className="text-[11px] text-birdie-charcoal/50 truncate">Next: “{c.next_subject}”</div>
        )}
      </div>
      {c.status === "active" && c.waiting_draft_id && (
        <Button size="sm" className="shrink-0 whitespace-nowrap" onClick={() => onReview(c.waiting_draft_id)}>
          Review
        </Button>
      )}
      {c.status === "active" ? (
        <Button
          size="sm"
          variant="secondary"
          className="shrink-0 whitespace-nowrap"
          onClick={() => onAction("stop")}
          disabled={Boolean(busy)}
        >
          Stop
        </Button>
      ) : (
        canStart && (
          <Button
            size="sm"
            variant="secondary"
            className="shrink-0 whitespace-nowrap"
            onClick={() =>
              window.confirm(
                `Start the no-reply follow-ups for ${lead.name || lead.email}? The first is drafted for your approval in a few days, unless you mark that they replied.`
              ) && onAction("start")
            }
            disabled={Boolean(busy)}
          >
            {c.status ? "Start again" : "Start"}
          </Button>
        )
      )}
    </div>
  );
}

const ICON = {
  inquiry: "📥",
  auto: "🤖",
  email: "✉️",
  quote: "📤",
  view: "👀",
  payment: "💰",
  touch: "📞",
  campaign: "⏱️",
};

// Newest first, so what happened last sits right under the buttons.
function Timeline({ items, onUndo }) {
  if (!items.length) return null;
  return (
    <ol className="space-y-2">
      {[...items].reverse().map((it, i) =>
        it.kind === "campaign" ? (
          <li key={i} className="text-center text-[11px] text-birdie-charcoal/45">
            {ICON.campaign} {it.title} · {formatEasternDateTime(it.at)}
          </li>
        ) : (
          <Entry key={i} it={it} onUndo={onUndo} />
        )
      )}
    </ol>
  );
}

function Entry({ it, onUndo }) {
  const [open, setOpen] = useState(false);
  const mine = it.direction === "out";
  return (
    <li className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[88%] rounded-2xl px-3 py-2 ${
          mine ? "bg-birdie-green/10 rounded-br-md" : "bg-birdie-cream rounded-bl-md"
        }`}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-xs font-semibold text-birdie-charcoal">
            {ICON[it.kind] || "•"} {it.title}
          </span>
          <span className="shrink-0 text-[10px] text-birdie-charcoal/45">{formatEasternDateTime(it.at)}</span>
        </div>
        {it.subject && <div className="text-sm text-birdie-charcoal mt-0.5">“{it.subject}”</div>}
        {it.body && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={`w-full text-left text-xs text-birdie-charcoal/70 mt-0.5 whitespace-pre-line ${open ? "" : "line-clamp-3"}`}
          >
            {it.body}
          </button>
        )}
        {it.removable && (
          <button type="button" onClick={() => onUndo(it)} className="mt-1 text-[10px] font-semibold text-birdie-charcoal/40 hover:text-red-700">
            Remove
          </button>
        )}
      </div>
    </li>
  );
}

function Chip({ href, to, children }) {
  const cls =
    "inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border border-birdie-green/20 text-birdie-green hover:bg-birdie-cream";
  return to ? (
    <Link to={to} className={cls}>
      {children}
    </Link>
  ) : (
    <a href={href} className={cls}>
      {children}
    </a>
  );
}
